// The drag handle on a right-hand panel's left edge (the coach panel, the session
// panel). The width is a CSS variable on <html>, so the panel and anything sized
// from it (Layout.jsx's margin beside the coach) move together without a
// re-render, and it is remembered on this browser. Keep the handle after the
// panel's content: inside a dialog, the first thing to focus shouldn't be it.
import React, { useEffect, useLayoutEffect, useRef } from 'react';

const STEP = 24;
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** A width the panel can take: never past `limits`, and always leaving `limits.room` of the window. */
export function clampPanelWidth(px, viewport, { min, max, room }) {
  const most = Math.min(max, Math.max(min, viewport - room));
  return Math.round(Math.min(most, Math.max(min, px)));
}

/** The width kept on this browser, or null (none kept, unreadable, or not a number). */
export function savedPanelWidth(storageKey) {
  let saved = null;
  try { saved = Number(localStorage.getItem(storageKey)); } catch { /* the standard width */ }
  return Number.isFinite(saved) && saved > 0 ? saved : null;
}


/**
 * `panelId`: the panel's element. `cssVar`: the variable its width reads.
 * `storageKey`: where the width is kept. `limits`: { min, standard, max, room }.
 */
export default function PanelResize({ panelId, cssVar, storageKey, limits, label }) {
  const handle = useRef(null);
  const apply = (px) => document.documentElement.style.setProperty(cssVar, `${px}px`);

  // On load and whenever the window changes size: a width kept on a wider
  // window (or a bigger screen) never pushes the panel, or the page beside it,
  // past the window's edge. The kept width itself is left alone, so a window
  // shrunk and widened again gets it back. With none kept, the CSS's standard
  // width stands (it fits every window the handle shows on, lg and up).
  useBeforePaint(() => {
    const fit = () => {
      const saved = savedPanelWidth(storageKey);
      if (saved) apply(clampPanelWidth(saved, window.innerWidth, limits));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [cssVar, storageKey, limits.min, limits.max, limits.room, limits.standard]);

  const current = () => document.getElementById(panelId)?.offsetWidth || limits.standard;
  const set = (px) => {
    const next = clampPanelWidth(px, window.innerWidth, limits);
    apply(next);
    try { localStorage.setItem(storageKey, String(next)); } catch { /* private window: this visit only */ }
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    handle.current?.setPointerCapture?.(e.pointerId);
    const move = (ev) => set(window.innerWidth - ev.clientX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';            // no text selected while dragging
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); set(current() + STEP); }
    if (e.key === 'ArrowRight') { e.preventDefault(); set(current() - STEP); }
  };

  return (
    <div ref={handle} role="separator" aria-orientation="vertical" aria-label={label} tabIndex={0}
      onPointerDown={onPointerDown} onKeyDown={onKeyDown}
      onDoubleClick={() => set(limits.standard)}
      className="group absolute inset-y-0 left-0 z-10 hidden w-2 cursor-col-resize touch-none lg:block focus:outline-none">
      <span className="absolute inset-y-0 left-0 w-0.5 bg-transparent transition-colors group-hover:bg-accent-default group-focus-visible:bg-accent-default" />
    </div>
  );
}
